/**
 * Context provider for the Warehouse Layout Editor.
 * Provides centralized state, dispatch, and derived data to all child components.
 */

import React, { createContext, useContext, useEffect, useCallback, useRef } from 'react';
import {
  LayoutEditorState,
  LayoutAction,
  useLayoutReducer,
} from '../hooks/useLayoutReducer';
import { RackDimensions, RackSummary, WarehouseLayout } from '../types/warehouseLayout';
import {
  getLayout,
  saveLayout,
  createEmptyLayout,
} from '../utils/layoutStorage';
import {
  calculatePreviewPositions,
} from '../utils/bulkPlacement';
import { calculateRackDimensions, getRotatedDimensions } from '../utils/positionCalculator';
import {
  serverToFrontendLayout,
  frontendToUpsertLayoutRequest,
  frontendToRackPositions,
  frontendToServerObjects,
} from '../utils/layoutApiMapping';
import { stcBinService } from '../../../../../services/stcBinService';
import {
  warehouseLayoutService,
  ServerRackGeometry,
  ServerBinPosition,
  ServerSpecialBin,
  ServerWarehouseInsights,
} from '../../../../../services/warehouseLayoutService';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface LayoutEditorContextValue {
  state: LayoutEditorState;
  dispatch: React.Dispatch<LayoutAction>;
  rackDimensionsMap: Map<string, RackDimensions>;
  canUndo: boolean;
  canRedo: boolean;
  save: () => void;
  warehouseId: string;
  companyNo: string;
  /** Object-URL for plantegning-underlaget (null = ingen) */
  backgroundUrl: string | null;
  uploadBackground: (file: File) => Promise<void>;
  removeBackground: () => Promise<void>;
  /** Re-hent ventende flytteordrer (feil → tom liste, blokkerer aldri) */
  refreshPendingMoves: () => void;
  /**
   * Kalles etter vellykket executeMove: re-henter ventende flytt OG invaliderer
   * innsikt-cachen (et flytt endrer fyllings-/tomme-bildet) med re-hent av aktiv modus.
   */
  notifyMoveExecuted: () => void;
}

const LayoutEditorContext = createContext<LayoutEditorContextValue | null>(null);

interface LayoutEditorProviderProps {
  warehouseId: string;
  companyNo: string;
  children: React.ReactNode;
}

export const LayoutEditorProvider: React.FC<LayoutEditorProviderProps> = ({
  warehouseId,
  companyNo,
  children,
}) => {
  const { state, dispatch, rackDimensionsMap, canUndo, canRedo } = useLayoutReducer();
  const { t } = useTranslation();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // Datataps-vern (kritisk-funn 1): server-skriving tillates KUN når layouten
  // faktisk ble lastet fra serveren — en fallback (API nede / tenant uten
  // migrasjon 060) skal aldri kunne replace-all-slette server-tilstanden.
  const serverLoadedRef = useRef(false);
  // Selve innlastingen endrer layout/binPositions og ville trigget autosave
  // 2 s etter mount (og persistert autoFit-krymping bare ved å åpne modulen).
  // Undertrykk nøyaktig den første effekten etter hver lasting.
  const suppressNextAutosaveRef = useRef(true);
  // Layout-radens LastUpdate = versjonsanker for samtidighetsvernet (kritisk-funn 2).
  // Alle posisjons-/objektskriv bumper den server-side; er den bumpet av en ANNEN
  // bruker siden vår siste lagring, svarer layout-PUT 409 og vi stopper skriving.
  const layoutVersionRef = useRef<string | null>(null);
  const [backgroundUrl, setBackgroundUrl] = React.useState<string | null>(null);
  // Cache for innsikt-responser — gjentatte modus-/dagvindu-bytter er gratis
  const insightsCacheRef = useRef<Map<string, ServerWarehouseInsights>>(new Map());
  // Bumpes når innsikt-cachen invalideres (etter utført flytt) — trigger re-hent av aktiv modus
  const [insightsRefreshNonce, setInsightsRefreshNonce] = React.useState(0);

  const replaceBackgroundUrl = (url: string | null) => {
    setBackgroundUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
  };

  const uploadBackground = async (file: File) => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.substring(result.indexOf(',') + 1)); // strip data:-prefiks
      };
      reader.onerror = () => reject(new Error('Kunne ikke lese fil'));
      reader.readAsDataURL(file);
    });

    // Fersk tenant/lager har ingen layout-rad før første lagring — og
    // plantegningen lagres PÅ layout-raden. Sørg for at den finnes først
    // (typisk førstegangsbruk: last opp tegningen som aller første handling).
    if (state.layout) {
      await saveToServer(state.layout, state.binPositions);
    }

    await warehouseLayoutService.updateBackground(warehouseId, {
      imageBase64: base64,
      imageType: file.type || 'image/png',
    });
    const objectUrl = URL.createObjectURL(file);
    replaceBackgroundUrl(objectUrl);

    // Tilpass gulvflatens sideforhold til tegningen slik at den ikke tegnes
    // forvrengt (bildet strekkes alltid til gulvet). Lengden beholdes; bredden
    // avledes av bildets sideforhold. Skala-kalibreringen etterpå skalerer
    // begge akser proporsjonalt til riktig målestokk.
    const currentLength = state.layout?.dimensions.length;
    if (currentLength) {
      const img = new Image();
      img.onload = () => {
        if (!img.naturalWidth || !img.naturalHeight) return;
        const aspect = img.naturalHeight / img.naturalWidth;
        const newWidth = Math.min(500, Math.max(5, Math.round(currentLength * aspect * 2) / 2));
        const currentWidth = state.layout?.dimensions.width ?? 0;
        // Ikke rør dimensjonene ved ubetydelig avvik (< 2 %)
        if (currentWidth > 0 && Math.abs(newWidth - currentWidth) / currentWidth > 0.02) {
          dispatch({ type: 'UPDATE_DIMENSIONS', field: 'width', value: newWidth });
        }
      };
      img.src = objectUrl;
    }
  };

  const removeBackground = async () => {
    await warehouseLayoutService.updateBackground(warehouseId, { imageBase64: null });
    replaceBackgroundUrl(null);
  };

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [warehouseId]);

  // Ventende flytteordrer (piler i 2D) — feil skal aldri blokkere editoren
  const refreshPendingMoves = useCallback(() => {
    warehouseLayoutService.getPendingMoves(warehouseId)
      .then(moves => dispatch({ type: 'SET_PENDING_MOVES', moves }))
      .catch(err => {
        console.warn('Kunne ikke hente ventende flytt:', err);
        dispatch({ type: 'SET_PENDING_MOVES', moves: [] });
      });
  }, [warehouseId, dispatch]);

  // Hent ved lasting — parallelt med (uavhengig av) layout/innsikt-hentingen
  useEffect(() => {
    refreshPendingMoves();
  }, [refreshPendingMoves]);

  // Etter vellykket executeMove (MoveConfirmDialog): pil-laget OG innsikt-overlayet
  // er begge utdaterte — re-hent flytt og tøm innsikt-cachen (nonce re-kjører
  // innsikt-effekten som nå får cache-miss og henter aktiv modus på nytt).
  const notifyMoveExecuted = useCallback(() => {
    refreshPendingMoves();
    insightsCacheRef.current.clear();
    setInsightsRefreshNonce(nonce => nonce + 1);
  }, [refreshPendingMoves]);

  // Hent innsiktsdata når overlay-modus eller dagvindu endres.
  // Cache per (lager, modus, dager) — feil beholder valgt modus, men nullstiller data.
  useEffect(() => {
    const mode = state.insightMode;
    if (mode === 'none') {
      dispatch({ type: 'SET_INSIGHTS_SUCCESS', insights: null });
      return;
    }

    const cacheKey = `${warehouseId}:${mode}:${state.insightDays}`;
    const cached = insightsCacheRef.current.get(cacheKey);
    if (cached) {
      dispatch({ type: 'SET_INSIGHTS_SUCCESS', insights: cached });
      return;
    }

    let cancelled = false;
    dispatch({ type: 'SET_INSIGHTS_START' });
    warehouseLayoutService.getInsights(warehouseId, mode, state.insightDays)
      .then(insights => {
        if (cancelled) return;
        insightsCacheRef.current.set(cacheKey, insights);
        dispatch({ type: 'SET_INSIGHTS_SUCCESS', insights });
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('Kunne ikke hente innsiktsdata:', err);
        dispatch({ type: 'SET_INSIGHTS_FAILURE' });
        dispatch({
          type: 'SET_ERROR',
          error: t('manager.modules.warehouseLayout.insights.loadFailed', {}, 'Kunne ikke hente innsiktsdata'),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [state.insightMode, state.insightDays, warehouseId, insightsRefreshNonce]);

  /**
   * Auto-fit warehouse dimensions to contain all placed racks with comfortable margins.
   * Only adjusts if the current dimensions are too large relative to rack footprint.
   */
  const autoFitDimensions = (layout: WarehouseLayout, summaries: RackSummary[], hasBackground: boolean): WarehouseLayout => {
    if (layout.racks.length === 0) return layout;

    // Build dimensions map from summaries
    const dimMap = new Map<string, RackDimensions>();
    summaries.forEach(s => dimMap.set(s.rackNumber, calculateRackDimensions(s)));

    // Calculate bounding box of all placed racks
    let maxX = 0, maxY = 0;
    layout.racks.forEach(rack => {
      const dim = dimMap.get(rack.rackNumber);
      if (!dim) return;
      const rotated = getRotatedDimensions(dim, rack.rotation);
      maxX = Math.max(maxX, rack.x + rotated.width / 2);
      maxY = Math.max(maxY, rack.y + rotated.depth / 2);
    });

    // Add margin (3m on each side for aisles and workspace)
    const MARGIN = 3;
    const neededLength = Math.ceil(maxX + MARGIN);
    const neededWidth = Math.ceil(maxY + MARGIN);

    const currentArea = layout.dimensions.length * layout.dimensions.width;
    const neededArea = neededLength * neededWidth;

    // Shrink if current dimensions are more than 2.5× larger than needed.
    // ALDRI når plantegning finnes: da er dimensjonene satt bevisst (kalibrert
    // målestokk) og skal ikke «optimaliseres» mot reol-fotavtrykket.
    if (!hasBackground && currentArea > neededArea * 2.5) {
      return {
        ...layout,
        dimensions: {
          ...layout.dimensions,
          length: Math.max(neededLength, 15), // Minimum 15m
          width: Math.max(neededWidth, 10),   // Minimum 10m
        },
      };
    }

    // Grow if racks extend beyond current dimensions
    if (neededLength > layout.dimensions.length || neededWidth > layout.dimensions.width) {
      return {
        ...layout,
        dimensions: {
          ...layout.dimensions,
          length: Math.max(layout.dimensions.length, neededLength),
          width: Math.max(layout.dimensions.width, neededWidth),
        },
      };
    }

    return layout;
  };

  const loadData = async () => {
    dispatch({ type: 'LOAD_DATA_START' });
    try {
      const defaultName = t('manager.modules.warehouseLayout.editor.defaultLayoutName', { '0': warehouseId }, `Lager ${warehouseId}`);
      let summaries = await stcBinService.getRackSummary(companyNo, warehouseId);

      // Datadreven geometri (StcBinSize) — feil her skal aldri blokkere editoren,
      // da faller vi bare tilbake til standard-heuristikken for dimensjoner.
      let serverGeometry: ServerRackGeometry[] = [];
      let specialBins: ServerSpecialBin[] = [];
      try {
        const geometry = await warehouseLayoutService.getGeometry(warehouseId);
        serverGeometry = geometry.racks;
        specialBins = geometry.specialBins;
      } catch (geoErr) {
        console.warn('Geometri fra server utilgjengelig — bruker heuristikk:', geoErr);
      }

      // Geometriens reol-liste er fasit (krever FULL template-parse). RackSummary
      // delvis-parser spesiallokasjoner ('bcl1' -> 'bc', 'CD001' -> 'CD') til
      // pseudo-reoler — de skal ikke tilbys som reoler i plasseringslistene,
      // de håndteres som enkeltlokasjoner (BinPosition) i stedet.
      if (serverGeometry.length > 0) {
        const realRacks = new Set(serverGeometry.map(g => g.rackNumber));
        summaries = summaries.filter(s => realRacks.has(s.rackNumber));
      }

      // Layout: server er sannhetskilden. localStorage brukes kun som
      // engangs-migrering (gamle utkast) og som fallback hvis API-et er nede.
      let storedLayout: WarehouseLayout | null = null;
      let binPositions: ServerBinPosition[] = [];
      let migrateLocalToServer = false;
      let hasBackground = false;
      try {
        const serverLayout = await warehouseLayoutService.getFullLayout(warehouseId);
        storedLayout = serverToFrontendLayout(warehouseId, serverLayout, defaultName);
        binPositions = serverLayout.binPositions;
        hasBackground = !!serverLayout.layout?.hasBackgroundImage;
        serverLoadedRef.current = true;
        layoutVersionRef.current = serverLayout.layout?.lastUpdate ?? null;

        if (serverLayout.layout?.hasBackgroundImage) {
          // Hent plantegningen i bakgrunnen — skal ikke blokkere editoren
          warehouseLayoutService.getBackgroundObjectUrl(warehouseId)
            .then(url => replaceBackgroundUrl(url))
            .catch(bgErr => console.warn('Kunne ikke hente plantegning:', bgErr));
        }
        if (!storedLayout) {
          const localLayout = getLayout(warehouseId);
          if (localLayout && localLayout.racks.length > 0) {
            // Migrér nettleser-lagret utkast til delt server-lagring
            storedLayout = localLayout;
            migrateLocalToServer = true;
          }
        }
      } catch (apiErr) {
        console.warn('Layout-API utilgjengelig — bruker localStorage (server-lagring er AV):', apiErr);
        serverLoadedRef.current = false;
        storedLayout = getLayout(warehouseId);
      }

      if (!storedLayout) {
        storedLayout = createEmptyLayout(warehouseId, defaultName);
      }

      // Auto-fit dimensions if warehouse is oversized relative to rack footprint
      storedLayout = autoFitDimensions(storedLayout, summaries, hasBackground);
      suppressNextAutosaveRef.current = true;
      dispatch({ type: 'LOAD_DATA_SUCCESS', layout: storedLayout, rackSummaries: summaries, serverGeometry, binPositions, specialBins });

      if (migrateLocalToServer) {
        saveToServer(storedLayout, binPositions).then(ok => {
          if (ok) console.info('Lokalt layout-utkast migrert til delt server-lagring');
        });
      }
    } catch (err) {
      console.error('Error loading data:', err);
      serverLoadedRef.current = false;
      suppressNextAutosaveRef.current = true;
      dispatch({
        type: 'LOAD_DATA_FAILURE',
        error: t('manager.modules.warehouseLayout.editor.loadDataFailed', {}, 'Kunne ikke laste lagerdata'),
        fallbackLayout: createEmptyLayout(warehouseId),
      });
    }
  };

  /** Lagrer layouten til delt server-lagring. Returnerer false ved feil (uten å kaste). */
  const saveToServer = async (layout: WarehouseLayout, binPositions: ServerBinPosition[]): Promise<boolean> => {
    // Aldri replace-all mot server når vi ikke lastet derfra (fallback-tilstand)
    if (!serverLoadedRef.current) {
      console.warn('Hopper over server-lagring: layouten ble ikke lastet fra serveren');
      return false;
    }
    try {
      const saved = await warehouseLayoutService.upsertLayout(
        layout.warehouseId,
        frontendToUpsertLayoutRequest(layout, layoutVersionRef.current)
      );
      await warehouseLayoutService.upsertRackPositions(
        layout.warehouseId, frontendToRackPositions(layout), true);
      await warehouseLayoutService.replaceObjects(
        layout.warehouseId, frontendToServerObjects(layout));
      await warehouseLayoutService.upsertBinPositions(
        layout.warehouseId, binPositions, true);
      // Posisjons-skrivene toucher layout-versjonen server-side med VÅR operatør —
      // same-operator-unntaket i 409-sjekken gjør at layout-PUT-responsen holder
      // som anker frem til en ANNEN bruker skriver.
      layoutVersionRef.current = saved.lastUpdate ?? layoutVersionRef.current;
      return true;
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      if (status === 409) {
        // En annen bruker har endret layouten — stopp all videre skriving
        // (hindrer at vårt utdaterte replace-all-snapshot sletter deres arbeid)
        serverLoadedRef.current = false;
        dispatch({
          type: 'SET_ERROR',
          error: t('manager.modules.warehouseLayout.editor.layoutConflict', {},
            'Layouten er endret av en annen bruker. Last modulen på nytt for å hente siste versjon — videre lagring er stoppet for å ikke overskrive.'),
        });
      }
      console.error('Kunne ikke lagre layout til server:', err);
      return false;
    }
  };

  // Auto-save on layout changes (inkl. plasserte enkeltlokasjoner)
  useEffect(() => {
    // Første kjøring etter (re)lasting er selve innlastingen — ikke en brukerendring
    if (suppressNextAutosaveRef.current) {
      suppressNextAutosaveRef.current = false;
      return;
    }
    if (state.layout && !state.loading) {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        save();
      }, 2000);
    }
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [state.layout, state.binPositions]);

  // Update preview positions when placement config or anchor changes
  useEffect(() => {
    if (state.layout && state.editorState.anchorRack && state.selectedUnplacedRacks.length > 0) {
      const anchorRack = state.layout.racks.find(r => r.rackNumber === state.editorState.anchorRack);
      if (anchorRack) {
        const previews = calculatePreviewPositions(
          anchorRack,
          state.selectedUnplacedRacks,
          rackDimensionsMap,
          state.layout.racks,
          state.layout.dimensions,
          state.editorState.placementConfig,
          state.layout.gridUnit
        );
        dispatch({ type: 'SET_PREVIEW_POSITIONS', positions: previews });
      }
    } else {
      dispatch({ type: 'SET_PREVIEW_POSITIONS', positions: [] });
    }
  }, [
    state.layout?.racks,
    state.editorState.anchorRack,
    state.editorState.placementConfig,
    state.selectedUnplacedRacks,
    rackDimensionsMap,
  ]);

  const save = useCallback(() => {
    if (!state.layout) return;
    const layout = state.layout;
    const binPositions = state.binPositions;
    dispatch({ type: 'SAVE_START' });

    // localStorage beholdes som lokal backup/offline-fallback
    try {
      saveLayout(layout);
    } catch (err) {
      console.warn('Kunne ikke lagre lokal backup:', err);
    }

    // Delt server-lagring er den autoritative lagringen
    saveToServer(layout, binPositions).then(ok => {
      if (ok) {
        dispatch({ type: 'SAVE_SUCCESS', at: new Date().toISOString() });
      } else {
        dispatch({ type: 'SAVE_FAILURE' });
      }
    });
  }, [state.layout, state.binPositions]);

  return (
    <LayoutEditorContext.Provider
      value={{
        state,
        dispatch,
        rackDimensionsMap,
        canUndo,
        canRedo,
        save,
        warehouseId,
        companyNo,
        backgroundUrl,
        uploadBackground,
        removeBackground,
        refreshPendingMoves,
        notifyMoveExecuted,
      }}
    >
      {children}
    </LayoutEditorContext.Provider>
  );
};

export function useLayoutEditor(): LayoutEditorContextValue {
  const ctx = useContext(LayoutEditorContext);
  if (!ctx) {
    throw new Error('useLayoutEditor must be used within a LayoutEditorProvider');
  }
  return ctx;
}
